// src/views/menage/ReglesAssignationSection.jsx
//
// « QUI S'OCCUPE DE QUOI PAR DÉFAUT » — l'écran où l'orchestrateur pose ses
// règles d'assignation (point 5 du chantier, demande Dimitri 2026-08-06).
//
// Une règle envoie automatiquement TOUS LES MÉNAGES FUTURS d'un logement chez
// un agent. Elle ne touche pas les ménages déjà créés — décision explicite de
// Dimitri : « Trop de surprise. On les réassigne à la main si besoin. » Comme
// les ménages sont créés la veille pour le lendemain, ça ne concerne jamais
// plus d'une journée, et l'écran le dit.
//
// ── PAS DE HIÉRARCHIE ─────────────────────────────────────────────────────────
//   L'admin et l'orchestrateur écrivent LA MÊME LIGNE, avec les mêmes droits.
//   Le dernier qui écrit gagne — mécaniquement, pas par arbitrage. D'où
//   l'importance d'afficher QUI a posé la règle en cours et QUAND : sans ça, on
//   écraserait le travail de l'autre à l'aveugle.
//
// ── CE QUE CET ÉCRAN NE MONTRE JAMAIS ─────────────────────────────────────────
//   Aucun montant. L'orchestrateur ne voit pas l'argent (ADR-227/228).

import { useState, useCallback } from 'react'
import useEtroit from '../../lib/useEtroit'
import RangeePastillesAgents from './RangeePastillesAgents'
import { Users, RotateCcw, AlertTriangle, Check } from 'lucide-react'
import { DS } from '../../lib/ui'
import { poserRegle, retirerRegle } from '../../lib/menage-regles-api'

// Date lisible sans heure : « qui et quand » suffit, la minute est du bruit.
// « 21 août » — la règle des dates (ni jour de semaine, ni année).
const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre']
function quand(iso) {
  if (typeof iso !== 'string' || iso.length < 10) return null
  const [, m, j] = iso.slice(0, 10).split('-')
  return `${Number(j)} ${MOIS_FR[Number(m) - 1] || ''}`
}

/** Une règle importée par la migration n'a pas d'auteur humain. */
function estReprise(regle) {
  const par = regle?.posee_par || ''
  return par.startsWith('migration:') || regle?.posee_par_role === 'systeme'
}

/** Auteur LISIBLE — jamais un email technique à l'écran (C-1) : le prénom
    de l'équipe si on le connaît, sinon un « l'équipe » honnête. */
function auteurLisible(regle, agents = []) {
  const email = regle?.posee_par || null
  if (!email) return null
  const connu = agents.find((a) => a.value === email)
  if (connu) return connu.label
  const avant = String(email).split('@')[0].split(/[._-]/)[0]
  return avant ? avant.charAt(0).toUpperCase() + avant.slice(1) : "l'équipe"
}

export default function ReglesAssignationSection({
  supabase, logements = [], agents = [], regles = {}, onChange,
}) {
  const [enCours, setEnCours] = useState(null)   // logement_id en cours d'écriture
  const [erreur, setErreur] = useState(null)
  const [succes, setSucces] = useState(null)

  const appliquer = useCallback(async (logementId, email) => {
    setEnCours(logementId); setErreur(null); setSucces(null)
    const r = email
      ? await poserRegle(supabase, logementId, email)
      : await retirerRegle(supabase, logementId)
    setEnCours(null)
    if (!r.ok) { setErreur(r.error); return }
    setSucces(logementId)
    if (typeof onChange === 'function') await onChange()
  }, [supabase, onChange])

  // TÉLÉPHONE (correction 22/08) : le choix d'agent au plus près de la ligne.
  const etroit = useEtroit()
  if (!logements.length) return null

  return (
    <div data-testid="regles-assignation" style={{ marginTop: 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
        <Users size={17} strokeWidth={1.9} color={DS.ciel} />
        <div style={{ fontFamily: DS.fontDisplay, fontSize: 17, fontWeight: 600, color: DS.marineDark }}>
          Qui s'occupe de quoi par défaut
        </div>
      </div>
      <div style={{ fontSize: 12, color: DS.textMuted, marginBottom: 12, lineHeight: 1.45, maxWidth: 620 }}>
        Les ménages <strong>à venir</strong> partiront automatiquement chez l'agent choisi.
        Les ménages <strong>déjà créés ne changent pas</strong> — ils sont créés la veille
        pour le lendemain, donc au plus une journée est concernée : réassignez-la à la main
        si besoin.
      </div>

      {erreur && (
        <div data-testid="regles-erreur" style={{
          display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 12px',
          background: DS.dangerBg, color: DS.dangerText, border: `0.5px solid ${DS.danger}55`,
          borderLeft: `3px solid ${DS.danger}`, borderRadius: 8, marginBottom: 10, fontSize: 12.5,
        }}>
          <AlertTriangle size={15} strokeWidth={1.9} style={{ flex: '0 0 auto', marginTop: 1 }} />
          <span>{erreur}</span>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {logements.map((log) => {
          const regle = regles[log.id] || null
          const auteur = auteurLisible(regle, agents)
          const date = quand(regle?.posee_le)
          const occupe = enCours === log.id
          return (
            <div key={log.id} data-testid={`regle-${log.id}`} style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '11px 13px', background: DS.card, borderRadius: DS.radiusCard,
              border: `0.5px solid ${DS.border}`, boxShadow: '0 1px 2px rgba(33,83,135,.05)',
            }}>
              <div style={{ flex: '1 1 190px', minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: DS.marine, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.nom}
                </div>
                {/* QUI et QUAND — sans ça on écrase le travail de l'autre à l'aveugle. */}
                <div data-testid={`regle-trace-${log.id}`} style={{ fontSize: 11, color: DS.textMuted, marginTop: 2 }}>
                  {!regle
                    ? 'Aucune règle — chaque ménage est à assigner à la main.'
                    : estReprise(regle)
                      ? `Reprise automatique${date ? ` le ${date}` : ''}`
                      : `Posée par ${auteur || 'inconnu'}${date ? ` le ${date}` : ''}`}
                </div>
              </div>

              {!etroit && (
              <select
                data-testid={`regle-select-${log.id}`}
                value={regle?.provider_email || ''}
                disabled={occupe}
                onChange={(e) => appliquer(log.id, e.target.value)}
                className="fbz-tap"
                style={{
                  padding: '7px 10px', borderRadius: DS.radiusControl,
                  border: `0.5px solid ${DS.border}`, background: DS.card,
                  color: DS.marine, fontSize: 12.5, fontFamily: DS.fontBody,
                  cursor: occupe ? 'wait' : 'pointer', maxWidth: 220,
                }}>
                <option value="">Aucun agent par défaut</option>
                {/* ⚠️ VU À L'ÉCRAN (2026-08-06), pas en test : quand l'admin pose
                    une règle vers un agent ABSENT de l'équipe de l'orchestrateur,
                    aucune option ne correspond et le navigateur retombe
                    silencieusement sur la première — « Aucun agent par défaut »
                    s'affichait sur un logement qui a bel et bien une règle, et
                    le moindre clic l'aurait effacée. On garde donc la valeur en
                    cours dans la liste, marquée, plutôt que de la masquer. */}
                {regle && !agents.some((a) => a.value === regle.provider_email) && (
                  <option value={regle.provider_email}>
                    {regle.provider_email} (hors de votre équipe)
                  </option>
                )}
                {agents.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
              )}

              {etroit && (
                <RangeePastillesAgents testPrefix={`regle-${log.id}`}
                  occupe={occupe}
                  actif={regle?.provider_email || ''}
                  onChoisir={(v) => appliquer(log.id, v)}
                  options={[
                    { value: '', label: 'Aucun' },
                    ...(regle && !agents.some((a) => a.value === regle.provider_email)
                      ? [{ value: regle.provider_email, label: `${regle.provider_email} (hors équipe)` }]
                      : []),
                    ...agents.map((a) => ({ value: a.value, label: a.label })),
                  ]} />
              )}

              {!etroit && regle && (
                <button
                  type="button"
                  data-testid={`regle-retirer-${log.id}`}
                  disabled={occupe}
                  onClick={() => appliquer(log.id, '')}
                  className="fbz-press"
                  title="Retirer la règle de ce logement"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '7px 10px', borderRadius: DS.radiusControl,
                    border: `0.5px solid ${DS.border}`, background: DS.card,
                    color: DS.textSecondary, fontSize: 12, fontWeight: 600,
                    fontFamily: DS.fontBody, cursor: occupe ? 'wait' : 'pointer',
                  }}>
                  <RotateCcw size={13} strokeWidth={2} /> Retirer
                </button>
              )}

              {succes === log.id && !occupe && (
                <span data-testid={`regle-ok-${log.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: DS.successText || DS.marine, fontWeight: 700 }}>
                  <Check size={13} strokeWidth={2.4} /> enregistré
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
